<template>
  <EnhancedDataTable
    ref="dataTable"
    title="Brands"
    :items="allBrands || []"
    :headers="headers"
    item-key="Brand_SK"
    selection-state-name="selectedBrandSks"
    :loading="allBrandsLoading ? 'info' : false"
    :initial-items-per-page="20"
    :footer-props="{ itemsPerPageOptions: [-1, 10, 15, 20, 25, 50, 100] }"
    show-select
    select-strategy="page"
    show-crud-actions
    show-reset-filters
    show-unselect-all
    entity-name="Brand"
    @add="openDialog()"
    @edit="openDialog($event)"
    @delete="handleDelete($event)"
    @merge="openMergeDialog($event)"
    max-height="calc(100vh - 180px)"
  />

  <v-dialog v-model="dialog" max-width="500px" @keydown.esc="close" persistent>
    <v-card>
      <v-card-title>
        <span class="text-h5">{{ formTitle }}</span>
      </v-card-title>

      <v-card-text>
        <v-container grid-list-md>
          <v-row wrap>
            <v-col cols="12">
              <v-text-field v-model="editedItem.Brand_Name" label="Brand Name" autofocus></v-text-field>
            </v-col>
            <v-col cols="12">
              <v-autocomplete
                :items="allHouses || []"
                item-title="House_Name"
                item-value="House_SK"
                v-model="editedItem.House_FK"
                label="House"
              ></v-autocomplete>
            </v-col>
          </v-row>
        </v-container>
      </v-card-text>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="primary" @click="save" class="pr-5">
          <v-icon start>mdi-content-save-outline</v-icon>Save
        </v-btn>
        <v-btn color="secondary" @click="close" class="pr-4">
          <v-icon start>mdi-close</v-icon>Cancel
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <MergeMasterDataDialog
    v-model="mergeDialog"
    :selected-records="selectedRecordsForMerge"
    :entity-name="'Brands'"
    item-key="Brand_SK"
    display-field="Brand_Name"
    :loading="mergeLoading"
    @merge="handleMerge"
  />

  <DeleteConfirmationDialog
    v-model="deleteDialog"
    entity-name="Brand"
    :item-name="itemToDelete?.Brand_Name"
    @confirm="confirmDelete"
  />
</template>

<script setup>
import { ref, computed } from 'vue'
import { useStore } from 'vuex'
import { useQuery, useMutation } from '@vue/apollo-composable'

import EnhancedDataTable from '@/components/EnhancedDataTable.vue'
import MergeMasterDataDialog from '@/components/MergeMasterDataDialog.vue'
import DeleteConfirmationDialog from '@/components/DeleteConfirmationDialog.vue'

import ALL_BRANDS from '@/graphql/Brand/allBrands.gql'
import ADD_BRAND from '@/graphql/Brand/addBrand.gql'
import UPDATE_BRAND from '@/graphql/Brand/updateBrand.gql'
import REMOVE_BRAND from '@/graphql/Brand/removeBrand.gql'
import MERGE_BRAND from '@/graphql/Brand/mergeBrand.gql'
import ALL_HOUSES from '@/graphql/House/allHouses.gql'

const store = useStore()

const {
  result: allBrandsResult,
  loading: allBrandsLoading,
} = useQuery(ALL_BRANDS)
const allBrands = computed(() => allBrandsResult.value?.allBrands)

const {
  result: allHousesResult,
} = useQuery(ALL_HOUSES)
const allHouses = computed(() => allHousesResult.value?.allHouses)

const headers = [
  { title: '#', key: 'Brand_SK', filterType: 'number', align: 'center' },
  { title: 'House', key: 'House.House_Name', filterType: 'combo', align: 'left' },
  { title: 'Name', key: 'Brand_Name', filterType: 'text', align: 'left' },
  { title: 'Actions', key: 'actions', sortable: false, align: 'center', width: '120px' }
]

const dataTable = ref(null)
const editedIndex = ref(-1)
const editedItem = ref({})
const formTitle = computed(() => editedIndex.value === -1 ? 'New Brand' : 'Edit Brand')
const dialog = ref(false)
const mergeDialog = ref(false)
const mergeLoading = ref(false)
const selectedRecordsForMerge = ref([])
const deleteDialog = ref(false)
const itemToDelete = ref(null)

const addError = (message) => store.commit('addError', message)
const addSuccess = (message) => store.commit('addSuccess', message)

const openDialog = (item = null) => {
  if (item) {
    editedIndex.value = allBrands.value?.indexOf(item) ?? -1
    editedItem.value = { ...item }
  } else {
    editedIndex.value = -1
    editedItem.value = {}
  }
  dialog.value = true
}

const close = () => {
  dialog.value = false
  setTimeout(() => {
    editedItem.value = {}
    editedIndex.value = -1
  }, 300)
}

const { mutate: addBrand } = useMutation(ADD_BRAND,
  () => ({
    update: (cache, { data: { addBrand } }) => {
      const { allBrands } = cache.readQuery({ query: ALL_BRANDS })
      const newData = { allBrands: [...allBrands, addBrand] }
      cache.writeQuery({ query: ALL_BRANDS, data: newData })
    }
  })
)

const { mutate: updateBrand } = useMutation(UPDATE_BRAND)

const save = () => {
  const input = {
    Brand_SK: editedItem.value.Brand_SK,
    Brand_Name: editedItem.value.Brand_Name,
    House_FK: editedItem.value.House_FK
  }
  if (editedIndex.value > -1) {
    updateBrand({ UpdateBrandInput: input })
      .then(() => close())
      .catch((error) => addError(error.message))
  } else {
    const { Brand_SK: _Brand_SK, ...addInput } = input
    addBrand({ AddBrandInput: addInput })
      .then(() => close())
      .catch((error) => addError(error.message))
  }
}

const { mutate: removeBrand } = useMutation(REMOVE_BRAND)

const handleDelete = (item) => {
  itemToDelete.value = item
  deleteDialog.value = true
}

const confirmDelete = () => {
  const item = itemToDelete.value
  if (!item) return

  removeBrand({
    Brand_SK: item.Brand_SK
  }, {
    update: (cache, { data: { removeBrand } }) => {
      const { allBrands } = cache.readQuery({ query: ALL_BRANDS })
      const newData = { allBrands: allBrands.filter((b) => b.Brand_SK !== removeBrand) }
      cache.writeQuery({ query: ALL_BRANDS, data: newData })
      // Remove deleted item from selection
      dataTable.value?.removeFromSelection(item.Brand_SK)
    }
  })
    .catch((error) => addError(error.message))
    .finally(() => {
      itemToDelete.value = null
    })
}

const openMergeDialog = (records) => {
  selectedRecordsForMerge.value = records
  mergeDialog.value = true
}

const { mutate: mergeBrand } = useMutation(MERGE_BRAND)

const handleMerge = (mergeData) => {
  mergeLoading.value = true
  mergeBrand({
    MergeBrandInput: {
      survivor_Brand_SK: mergeData.survivor,
      duplicate_Brand_SKs: mergeData.duplicates
    }
  }, {
    update: (cache) => {
      const { allBrands } = cache.readQuery({ query: ALL_BRANDS })
      const newData = {
        allBrands: allBrands.filter(brand => !mergeData.duplicates.includes(brand.Brand_SK))
      }
      cache.writeQuery({ query: ALL_BRANDS, data: newData })
    }
  })
    .then(() => {
      // Remove merged duplicates from selection
      dataTable.value?.removeFromSelection(mergeData.duplicates)
      mergeDialog.value = false
      addSuccess(`Successfully merged ${mergeData.duplicates.length + 1} brands`)
    })
    .catch((error) => addError(error.message))
    .finally(() => {
      mergeLoading.value = false
    })
}
</script>
